import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eraser, PenLine, Trash2, Undo2 } from 'lucide-react';
import type * as Y from 'yjs';
import {
  SKETCH_COLOR_SWATCHES,
  sketchDocName,
  STROKE_WIDTHS,
  type AppToolMeta,
  type Stroke,
} from '@awwyeah/shared';
import { useDocLocalLoaded, usePresence, useYArray, useYDoc } from '../../sync';
import { useUser } from '../auth';
import { useAppToolMeta } from './useAppToolRegistries';
import { newId, nowIso } from './appToolOps';
import { clearCanvas, drawStroke, fitCanvasToDpr, redrawAll } from './sketch/canvas';
import { createRepaintController, type RepaintController } from './sketch/repaintController';
import {
  appendStroke,
  deleteStrokeById,
  findStrokeAt,
  undoLastOwnStroke,
} from './sketch/strokeOps';

type Tool = 'pen' | 'eraser';

/**
 * SketchPad (WS-16, the marquee feature). Live collaborative drawing at **stroke**
 * granularity, not point granularity:
 *
 *  - While a stroke is in progress it is drawn locally only, straight onto a **preview**
 *    canvas from pointer events (coalesced onto `requestAnimationFrame`, ~display refresh
 *    rate) — nothing touches Yjs yet, so there is nothing to merge and nothing to conflict.
 *  - On `pointerup` the finished stroke — one small object with its full point list — is
 *    pushed onto the `strokes` `Y.Array` in a single transaction.
 *
 * Streaming partial strokes through Yjs (a point at a time) was considered and rejected: it
 * would multiply update traffic ~60x per stroke, make an offline-drawn stroke reassemble as
 * a pile of point-inserts on reconnect instead of one clean append, and buys nothing the
 * household actually asked for ("draw and see each other draw", not "see the pen tip
 * mid-stroke"). Stroke-granularity is simple, conflict-free (two members' concurrent
 * strokes are just two array entries) and merges perfectly offline (an offline stroke is
 * one `push` replayed on reconnect, not a partial one needing repair).
 *
 * **Two canvases, one repaint authority each** (rewritten 2026-08-02 — see
 * docs/DECISIONS.md for the field bug this fixes). Committed strokes and the in-progress
 * preview used to share one canvas, repainted from a `useEffect(() => scheduleRedraw(),
 * [items])` plus a *second*, independent copy of the same redraw logic living inside a
 * `ResizeObserver` callback bound once on mount. That second copy closed over the very
 * first render's `items` (almost always still empty, pre-hydration) and never updated,
 * because its effect had a `[]` dependency array — every later resize (an iOS toolbar
 * hide/show, a keyboard opening) replayed that stale, empty redraw and wiped out real
 * strokes. Now:
 *
 *  - The **committed** canvas is repainted by exactly one `RepaintController`
 *    (`committedRepaint`), whose `paint` reads the live `Y.Array` (`arrayRef.current`)
 *    directly — never a React-state snapshot — so it cannot go stale relative to a render.
 *    It is scheduled from the `Y.Array` observer (any change: mine, a housemate's, an
 *    offline replay, or IndexedDB hydration applying its update — all fire the same
 *    observer), from `useDocLocalLoaded` (belt-and-braces explicit hydration signal), and
 *    `flush()`ed synchronously from the resize path so a backing-store resize (which clears
 *    the bitmap — HTML canvas spec, not a bug) never leaves a cleared frame on screen.
 *  - The **preview** canvas is a separate, purely-local overlay for the in-progress stroke,
 *    so clearing it on `pointerup` can never erase a committed stroke — and `pointerup`
 *    itself commits to the `Y.Array` and `flush()`es the committed repaint *before* clearing
 *    the preview, so there is never a frame with neither the preview line nor the committed
 *    one (the "line vanishes when you lift your finger" symptom).
 *
 * Full redraw (not incremental diffing) is fine at family scale (dozens of strokes) and
 * dramatically simpler; it's just no longer entangled with resize/hydration scheduling.
 */
export function SketchPadPage(): JSX.Element {
  const { toolId } = useParams<{ toolId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const { meta, map } = useAppToolMeta(toolId);
  const docName = toolId ? sketchDocName(toolId) : sketchDocName('missing');
  const doc = useYDoc(docName);
  const { array, items } = useYArray<Stroke>(doc, 'strokes');
  const locallyLoaded = useDocLocalLoaded(docName);

  const others = usePresence(
    docName,
    user && toolId ? { userId: user.id, name: user.name, color: user.color } : null,
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const committedCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const committedCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const previewCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const sizeRef = useRef({ width: 1, height: 1 });
  const drawingRef = useRef(false);
  const currentPointsRef = useRef<number[]>([]); // pixel coords while a pen stroke is live
  const eraseDragRef = useRef(false);

  // "Latest ref" mirrors of render-scoped values that the long-lived resize/observer
  // effects (bound once, never re-running) need to read fresh — see the module doc comment
  // for why closing over the render values directly, instead of these refs, is exactly the
  // bug that made strokes disappear on resize.
  const arrayRef = useRef<Y.Array<Stroke>>(array);
  arrayRef.current = array;
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState<string>(user?.color ?? (SKETCH_COLOR_SWATCHES[0] as string));
  const [width, setWidth] = useState<number>(STROKE_WIDTHS[1]);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const colorRef = useRef(color);
  colorRef.current = color;
  const widthRef = useRef(width);
  widthRef.current = width;
  const userRef = useRef(user);
  userRef.current = user;

  const swatches = [user?.color, ...SKETCH_COLOR_SWATCHES].filter(
    (c, i, all): c is string => Boolean(c) && all.indexOf(c) === i,
  );

  const normalizeCurrent = (w: number, h: number): number[] => {
    const pixels = currentPointsRef.current;
    const out: number[] = [];
    for (let i = 0; i + 1 < pixels.length; i += 2) {
      out.push((pixels[i] ?? 0) / w, (pixels[i + 1] ?? 0) / h);
    }
    return out;
  };

  // The committed layer's *only* repaint authority: always redraws straight from the live
  // `Y.Array`, never a value captured at some earlier render, so it can be safely called
  // from long-lived callbacks (the resize effect below) without going stale.
  const committedRepaintRef = useRef<RepaintController | null>(null);
  committedRepaintRef.current ??= createRepaintController(() => {
    const ctx = committedCtxRef.current;
    if (!ctx) return;
    const { width: w, height: h } = sizeRef.current;
    redrawAll(ctx, arrayRef.current.toArray(), w, h);
  });
  const committedRepaint = committedRepaintRef.current;

  // The preview layer's repaint authority: draws only the in-progress local stroke (or
  // nothing, mid-idle) onto its own overlay canvas, so clearing it can never touch — let
  // alone erase — a committed stroke on the layer underneath.
  const previewRepaintRef = useRef<RepaintController | null>(null);
  previewRepaintRef.current ??= createRepaintController(() => {
    const ctx = previewCtxRef.current;
    if (!ctx) return;
    const { width: w, height: h } = sizeRef.current;
    clearCanvas(ctx, w, h);
    if (drawingRef.current && currentPointsRef.current.length >= 2) {
      drawStroke(
        ctx,
        { points: normalizeCurrent(w, h), width: widthRef.current, color: colorRef.current },
        w,
        h,
      );
    }
  });
  const previewRepaint = previewRepaintRef.current;

  // (Re)size both canvases to their shared container and repaint atomically — resizing a
  // canvas's backing store clears its bitmap (HTML canvas spec), so `flush()` (synchronous,
  // not rAF-deferred) repaints in the very same tick, never leaving a cleared frame visible.
  // Also re-runs on `visibilitychange`/`pageshow` (iOS PWA restore can hand back a canvas
  // whose context/bitmap didn't survive being backgrounded). Bound once on mount: everything
  // it reads is either a ref or one of the two `RepaintController`s (stable for the
  // component's lifetime), so — unlike the bug this replaced — nothing here can go stale.
  useEffect(() => {
    const container = containerRef.current;
    const committedCanvas = committedCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!container || !committedCanvas || !previewCanvas) return undefined;

    const resize = (): void => {
      const rect = container.getBoundingClientRect();
      sizeRef.current = { width: rect.width, height: rect.height };
      committedCtxRef.current = fitCanvasToDpr(committedCanvas, rect.width, rect.height);
      previewCtxRef.current = fitCanvasToDpr(previewCanvas, rect.width, rect.height);
      committedRepaint.flush();
      previewRepaint.flush();
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') resize();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', resize);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', resize);
    };
  }, [committedRepaint, previewRepaint]);

  // The committed canvas's repaint trigger: any `Y.Array` change at all — mine, a
  // housemate's, or IndexedDB hydration applying its stored update (that's a change like
  // any other, so this already covers "the doc just finished loading locally"). Runs
  // immediately on mount/array-change too, in case data is already in memory.
  useEffect(() => {
    committedRepaint.schedule();
    const onChange = (): void => committedRepaint.schedule();
    array.observe(onChange);
    return () => array.unobserve(onChange);
  }, [array, committedRepaint]);

  // Belt-and-braces explicit hydration signal (docs/ARCHITECTURE.md's offline-first local
  // load) — redundant with the observer above in the common case, but names the trigger
  // explicitly rather than relying solely on "hydration happens to fire an observer".
  useEffect(() => {
    if (locallyLoaded) committedRepaint.schedule();
  }, [locallyLoaded, committedRepaint]);

  const pointFromEvent = (e: React.PointerEvent<HTMLDivElement>): [number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const eraseAt = (px: number, py: number): void => {
    const { width: w, height: h } = sizeRef.current;
    const nx = px / w;
    const ny = py / h;
    const hit = findStrokeAt(items, nx, ny);
    if (hit) {
      deleteStrokeById(array, hit.id);
      committedRepaint.flush(); // immediate feedback — an erase is itself a "commit"
    }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = pointFromEvent(e);
    if (tool === 'eraser') {
      eraseDragRef.current = true;
      eraseAt(x, y);
      return;
    }
    drawingRef.current = true;
    currentPointsRef.current = [x, y];
    previewRepaint.schedule();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (tool === 'eraser') {
      if (!eraseDragRef.current) return;
      const [x, y] = pointFromEvent(e);
      eraseAt(x, y);
      return;
    }
    if (!drawingRef.current) return;
    // Coalesced events (where supported) give sub-frame sampling without extra listeners;
    // falls back to the single move event on browsers without the API.
    const events =
      typeof e.nativeEvent.getCoalescedEvents === 'function'
        ? e.nativeEvent.getCoalescedEvents()
        : [e.nativeEvent];
    const rect = e.currentTarget.getBoundingClientRect();
    for (const evt of events.length > 0 ? events : [e.nativeEvent]) {
      currentPointsRef.current.push(evt.clientX - rect.left, evt.clientY - rect.top);
    }
    previewRepaint.schedule();
  };

  const finishStroke = (): void => {
    eraseDragRef.current = false;
    if (!drawingRef.current) return;
    const { width: w, height: h } = sizeRef.current;
    const points = normalizeCurrent(w, h);
    if (points.length >= 4 && userRef.current) {
      const stroke: Stroke = {
        id: newId(),
        authorId: userRef.current.id,
        color: colorRef.current,
        width: widthRef.current,
        points,
        createdAt: nowIso(),
      };
      doc.transact(() => appendStroke(arrayRef.current, stroke));
      // Commit FIRST, then flush the committed repaint synchronously — before the preview
      // is cleared below — so there is never a frame with neither the preview stroke nor
      // the just-committed one (the "line vanishes on lift" symptom this fixes).
      committedRepaint.flush();
    }
    // Only now clear the live/in-progress state and the preview layer: the committed layer
    // (if anything was committed) already shows it, so this can't leave a gap. For a stray
    // tap-with-no-drag, nothing was ever committed and this just drops it silently.
    drawingRef.current = false;
    currentPointsRef.current = [];
    previewRepaint.flush();
  };

  const undo = (): void => {
    if (!user) return;
    doc.transact(() => undoLastOwnStroke(array, user.id));
    committedRepaint.flush();
  };

  const clearAll = (): void => {
    doc.transact(() => {
      if (array.length > 0) array.delete(0, array.length);
    });
    committedRepaint.flush();
    setConfirmingClear(false);
  };

  const renameTo = (title: string): void => {
    if (!meta || !map || !user || !title.trim()) return;
    map.set(meta.id, {
      ...meta,
      title: title.trim(),
      updatedBy: user.id,
      updatedAt: new Date().toISOString(),
    } satisfies AppToolMeta);
  };

  if (!toolId) return <></>;

  return (
    <div
      className="flex min-h-[calc(100dvh-11.5rem)] flex-col gap-2 py-3 lg:min-h-[calc(100dvh-7rem)]"
      data-testid="sketchpad-page"
    >
      <header className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Back to Apps"
          data-testid="sketchpad-back"
          onClick={() => navigate('/apps')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <ArrowLeft size={20} />
        </button>
        <input
          data-testid="sketchpad-title-input"
          className="h-11 min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 text-xl font-bold text-fg focus:border-line-strong focus:bg-bg"
          value={meta?.title ?? ''}
          placeholder="Untitled sketch"
          onChange={(e) => renameTo(e.target.value)}
        />
        <div className="flex -space-x-2" data-testid="sketchpad-presence">
          {others.map((presence) => (
            <span
              key={presence.userId}
              title={presence.name}
              data-testid="presence-dot"
              className="h-6 w-6 flex-shrink-0 rounded-full border-2 border-surface"
              style={{ background: presence.color }}
            />
          ))}
        </div>
      </header>

      <div
        ref={containerRef}
        data-testid="sketchpad-canvas-wrap"
        className="relative min-h-[50vh] flex-1 touch-none overflow-hidden rounded-md border border-line bg-bg"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
        onPointerLeave={(e) => {
          // Only a real "left the canvas without lifting" case for a mouse; a captured
          // pointer (finger/pen mid-stroke) keeps delivering move/up events regardless.
          if (e.pointerType === 'mouse') finishStroke();
        }}
      >
        {/* Committed strokes — repainted only by `committedRepaint`, from the live
            `Y.Array`. Pointer events pass through to the wrapping div: hit-testing which
            canvas the finger is "on" doesn't matter since both fill the container. */}
        <canvas
          ref={committedCanvasRef}
          data-testid="sketchpad-canvas"
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
        {/* In-progress stroke overlay — its own bitmap so clearing it on pointerup can
            never erase a committed stroke underneath. */}
        <canvas
          ref={previewCanvasRef}
          data-testid="sketchpad-preview-canvas"
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-line bg-surface p-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="tool-pen"
              aria-pressed={tool === 'pen'}
              onClick={() => setTool('pen')}
              className={`flex h-11 w-11 items-center justify-center rounded-full ${
                tool === 'pen' ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
              }`}
            >
              <PenLine size={18} />
            </button>
            <button
              type="button"
              data-testid="tool-eraser"
              aria-pressed={tool === 'eraser'}
              onClick={() => setTool('eraser')}
              className={`flex h-11 w-11 items-center justify-center rounded-full ${
                tool === 'eraser' ? 'bg-inverse text-inverse-fg' : 'border border-line text-fg-2'
              }`}
            >
              <Eraser size={18} />
            </button>
          </div>
          <div className="flex gap-2">
            {STROKE_WIDTHS.map((w, i) => (
              <button
                key={w}
                type="button"
                data-testid={`width-${i}`}
                aria-pressed={width === w}
                onClick={() => setWidth(w)}
                className={`flex h-11 w-11 items-center justify-center rounded-full ${
                  width === w ? 'border-2 border-line-strong bg-raised' : 'border border-line'
                }`}
              >
                <span
                  className="rounded-full bg-fg"
                  style={{ width: 6 + i * 5, height: 6 + i * 5 }}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Undo my last stroke"
              data-testid="sketch-undo"
              onClick={undo}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-fg-2"
            >
              <Undo2 size={18} />
            </button>
            <button
              type="button"
              aria-label="Clear all"
              data-testid="sketch-clear"
              onClick={() => setConfirmingClear(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-danger"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="sketch-color-swatches">
          {swatches.map((swatch) => (
            <button
              key={swatch}
              type="button"
              aria-label={`Color ${swatch}`}
              aria-pressed={color === swatch}
              data-testid="sketch-color-swatch"
              onClick={() => setColor(swatch)}
              className={`h-11 w-11 flex-shrink-0 rounded-full ${
                color === swatch ? 'ring-2 ring-offset-2 ring-line-strong' : 'border border-line'
              }`}
              style={{ background: swatch }}
            />
          ))}
        </div>
      </div>

      {confirmingClear && (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
          data-testid="clear-all-confirm"
        >
          <div className="w-full max-w-sm rounded-t-lg border-t border-line bg-surface p-4 sm:rounded-lg sm:border">
            <p className="pb-4 text-base text-fg">Clear the whole sketch for everyone?</p>
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="confirm-clear-all"
                onClick={clearAll}
                className="h-12 flex-1 rounded-md bg-danger text-base font-semibold text-surface"
              >
                Clear it
              </button>
              <button
                type="button"
                onClick={() => setConfirmingClear(false)}
                className="h-12 flex-1 rounded-md border-2 border-line-strong text-base font-semibold text-fg"
              >
                Keep it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
